/**
 * Watches the conversation's currentCheckpointId and, when it changes,
 * captures the current viewer frame and uploads it as the checkpoint's
 * thumbnail. Runs only when the viewer has rendered at least one frame.
 *
 * Mount once near the top of the page tree (inside EditorView / ChatSection).
 */

import { useEffect, useRef } from 'react';
import { useConversation } from '@/services/conversationService';
import { captureViewerThumbnail } from '@/lib/viewerCapture';
import { uploadCheckpointThumbnail, useCheckpoints } from '@/services/checkpointService';
import { useParams } from 'react-router-dom';

export function useAutoThumbnailCapture() {
  const { id: conversationId } = useParams();
  const { conversation } = useConversation();
  const { data: checkpoints = [] } = useCheckpoints(conversationId);
  const attemptedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const ckptId = conversation?.currentCheckpointId;
    if (!conversationId || !ckptId) return;
    // Only attempt once per checkpoint per session.
    if (attemptedRef.current.has(ckptId)) return;
    // Skip if the checkpoint already has a thumbnail.
    const found = checkpoints.find((c) => c.id === ckptId);
    if (!found) return; // Not loaded yet — try again on next checkpoint list update.
    if (found.thumbnailStoragePath) {
      attemptedRef.current.add(ckptId);
      return;
    }

    // Wait a couple of animation frames so the viewer has actually drawn
    // the new geometry before we capture.
    let raf1 = 0;
    let raf2 = 0;
    let timeout = 0;
    const attempt = async () => {
      const dataUrl = captureViewerThumbnail(256);
      if (!dataUrl) {
        console.log('[autoThumb] capture returned null — viewer not ready yet');
        return;
      }
      attemptedRef.current.add(ckptId);
      try {
        await uploadCheckpointThumbnail({
          conversationId,
          checkpointId: ckptId,
          dataUrl,
        });
        console.log(`[autoThumb] uploaded thumbnail for ${ckptId}`);
      } catch (e) {
        console.warn('[autoThumb] upload failed:', e);
      }
    };
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        // Small delay to let the STL load + compile finish.
        timeout = window.setTimeout(attempt, 1500);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(timeout);
    };
  }, [conversation?.currentCheckpointId, conversationId, checkpoints]);
}
